import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';

import { isEmptyArray, isNullish } from '../../common/utils/nullish';
import {
  formatNumberToShowSign,
  formatToNDecimalPlaces,
} from '../../common/utils/formatNumber';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './purchases-and-sales-chart.constants.js';
import type { PurchasesAndSalesChartContentObject } from './purchases-and-sales-chart.model.js';
import { restrictLastLegendItemClick } from '../../common/utils/sharedChartOptions.js';

/**
 * @tag pop-purchases-and-sales-chart-component
 * @attribute { PurchasesAndSalesChartContentObject } [data-component-object] PurchasesAndSalesChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopPurchasesAndSalesChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * PurchasesAndSalesChart content object
   */
  componentObject?: PurchasesAndSalesChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');

    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getCategories() {
    return (
      this.componentObject?.itemList?.map((item) =>
        item.name ? item.name : '',
      ) || []
    );
  }

  private getSeriesData() {
    const portfolioWeightSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.percentTotalNetAssets,
        name: item.name,
        change: `${formatNumberToShowSign(item.previousQuarterChange)}%`,
      })) || [];

    const prevQuarterPortfolioWeightSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.previousQuarterPercentTotalNetAssets,
        name: item.name,
        change: `${formatNumberToShowSign(item.previousQuarterChange)}%`,
      })) || [];

    return [
      {
        name: Constants.PREV_QUARTER_PORTFOLIO_WEIGHT_LABEL,
        data: prevQuarterPortfolioWeightSeries,
      },
      { name: Constants.PORTFOLIO_WEIGHT_LABEL, data: portfolioWeightSeries },
    ];
  }

  private getOptions(): Options {
    return {
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter() {
          return BeaconChartBase.tooltipPointHTML(
            this,
            this.series.name,
            `${formatToNDecimalPlaces(this.y, 2)}%`,
          );
        },
        footerFormat: BeaconChartBase.tooltipFooterHTML(
          BeaconChartBase.tooltipFooterContentHTML(
            'Change from previous quarter <span class="b-font-weight-bold">{point.point.change}</span>',
          ),
        ),
      },
      exporting: { enabled: false },
      legend: restrictLastLegendItemClick(),
      xAxis: { categories: this.getCategories() },
      yAxis: { max: this.componentObject?.maxYAxisValue },
    };
  }

  render() {
    if (
      isEmptyArray(this.componentObject?.itemList) ||
      isNullish(this.componentObject?.itemList)
    ) {
      return html``;
    }
    return this.renderComponent(
      html`<beacon-bar-chart
        variant="vertical"
        tooltip-variant="multi-level"
        series="${JSON.stringify(this.getSeriesData())}"
        options="${JSON.stringify(this.getOptions())}"
        y-axis-label="${Constants.Y_AXIS_LABEL}"
        x-axis-label="${Constants.ISSUER_LABEL}"
      >
      </beacon-bar-chart>`,
    );
  }
}
